import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api.js';
import EventFormModal from './EventFormModal.jsx';
import StatusBadge from './StatusBadge.jsx';

const STATUSES = ['offen', 'in Arbeit', 'erledigt'];

export default function Events() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const mandantFilter = searchParams.get('mandant') || '';

    const [events, setEvents] = useState([]);
    const [mandanten, setMandanten] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [creating, setCreating] = useState(false);

    function setMandantFilter(value) {
        // URL-Query-Param als Source of Truth: erlaubt Bookmarks/Sharing
        // und übersteht Re-Renders ohne zusätzlichen Component-State.
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (value) next.set('mandant', value);
                else next.delete('mandant');
                return next;
            },
            { replace: true },
        );
    }

    async function reload() {
        try {
            setError('');
            const [e, m] = await Promise.all([
                api.listEvents({ search, status: statusFilter, mandant_id: mandantFilter }),
                mandanten.length === 0 ? api.listMandanten() : Promise.resolve(mandanten),
            ]);
            setEvents(e);
            if (mandanten.length === 0) setMandanten(m);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    // Debounced reload bei Such- oder Filteränderung.
    useEffect(() => {
        const t = setTimeout(reload, 200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, statusFilter, mandantFilter]);

    async function handleStatusChange(id, status) {
        try {
            await api.updateEventStatus(id, status);
            setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
        } catch (err) {
            alert('Status-Update fehlgeschlagen: ' + err.message);
        }
    }

    async function handleDelete(id) {
        if (!window.confirm('Dieses Ereignis wirklich löschen?')) return;
        try {
            await api.deleteEvent(id);
            await reload();
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    if (loading) return <div className="empty">Lade…</div>;

    return (
        <div>
            <h1>Ereignisse</h1>
            {error && <div className="panel" style={{ color: 'var(--color-danger)' }}>Fehler: {error}</div>}

            <div className="toolbar">
                <input
                    type="text"
                    placeholder="Suche in Titel, Beschreibung, Autor…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 340 }}
                />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">Alle Status</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={mandantFilter} onChange={(e) => setMandantFilter(e.target.value)}>
                    <option value="">Alle Mandanten</option>
                    {mandanten.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <div className="spacer" />
                <button className="btn btn--primary" onClick={() => setCreating(true)}>
                    Neues Ereignis
                </button>
            </div>

            <div className="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Titel</th>
                            <th>Mandant</th>
                            <th>Status</th>
                            <th>Autor</th>
                            <th>Beschreibung</th>
                            <th>Datum</th>
                            <th style={{ width: 1 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.length === 0 ? (
                            <tr><td colSpan={7} className="empty">Keine Ereignisse gefunden.</td></tr>
                        ) : events.map((e) => (
                            <tr
                                key={e.id}
                                className="row-clickable"
                                onClick={() => navigate(`/events/${e.id}`)}
                            >
                                <td>{e.title}</td>
                                <td>{e.mandant_name || <span className="muted">–</span>}</td>
                                <td>
                                    {/* stopPropagation: Status inline ändern, ohne die Zeilen-Navigation auszulösen */}
                                    <select
                                        className="status-select"
                                        value={e.status}
                                        onClick={(ev) => ev.stopPropagation()}
                                        onChange={(ev) => handleStatusChange(e.id, ev.target.value)}
                                    >
                                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <div style={{ marginTop: 4 }}><StatusBadge status={e.status} /></div>
                                </td>
                                <td>{e.author || <span className="muted">–</span>}</td>
                                <td style={{ maxWidth: 380, whiteSpace: 'pre-wrap' }}>
                                    {e.description || <span className="muted">–</span>}
                                </td>
                                <td>{formatDate(e.created_at)}</td>
                                <td>
                                    <button
                                        className="btn btn--sm btn--danger"
                                        onClick={(ev) => { ev.stopPropagation(); handleDelete(e.id); }}
                                    >
                                        Löschen
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {creating && (
                <EventFormModal
                    mandanten={mandanten}
                    onClose={() => setCreating(false)}
                    onSaved={async () => { setCreating(false); await reload(); }}
                />
            )}
        </div>
    );
}

function formatDate(s) {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('de-DE');
}
